import { useEffect, useState } from 'react';
import { ReactComponent as IllustrationAccountNotExisting } from 'assets/images/illustration_error_account_not_existing.svg';

import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { Typography } from '@mui/material';
import { DotSpinner } from '@crono-one/design-system';
import {
  CancelButton,
  MainButton,
} from 'crono-fe-common/components/CronoButton';
import useGetProspect from 'hooks/services/prospect/useGetProspect';
import { createPortal } from 'react-dom';
import CompanyAssignModal from 'pages/accountDrawer/contactView/companyAssignModal';
import AddCompanyDrawer from 'components/CreationForms/forms/AddCompanyDrawer';
import { useAccountDrawer } from 'context/accountDrawer';
import TaskExecutionFooterActions from 'pages/accountDrawer/TaskExecutionFooterActions';
import { useAfterContactReassigned } from 'pages/accountDrawer/hooks/useAfterContactReassigned';

interface CompanyDeletedDrawerViewProps {
  selectedProspectId: string | undefined;
}

const CompanyDeletedDrawerView = ({
  selectedProspectId,
}: CompanyDeletedDrawerViewProps) => {
  const { data: prospect } = useGetProspect(selectedProspectId);
  const { drawerContent, updateDrawerContent } = useAccountDrawer();
  const afterContactReassigned = useAfterContactReassigned();
  const [showCompanyAssign, setShowCompanyAssign] = useState<boolean>(false);
  const [showCompanyCreateAssign, setShowCompanyCreateAssign] =
    useState<boolean>(false);
  const [initialNameCreateNewAccount, setInitialNameCreateNewAccount] =
    useState<string>('');

  // This panel replaces the whole task view, so without the task actions a
  // single broken company would hard-stop the tasks flow.
  const taskId = drawerContent?.task?.id;

  // The contact can already belong to a live company while the drawer is still
  // pinned to the dead one it was opened with — a CRM merge remaps the contact,
  // or the user just re-assigned it here. The objectId check ignores the
  // keepPreviousData leftover of a contact switch.
  const currentAccountId =
    prospect?.data.data?.objectId === selectedProspectId
      ? prospect?.data.data?.accountId
      : undefined;
  const isDrawerOnPreviousAccount =
    !!currentAccountId && currentAccountId !== drawerContent?.accountId;

  // Follow the contact instead of asking to relink a company it already left.
  // Only the account changes, so a running tasks flow (which cancels itself on
  // a modal/task change) survives it.
  useEffect(() => {
    if (isDrawerOnPreviousAccount && currentAccountId) {
      updateDrawerContent({ accountId: currentAccountId });
    }
  }, [isDrawerOnPreviousAccount, currentAccountId, updateDrawerContent]);

  if (isDrawerOnPreviousAccount) {
    return (
      <div className="size-full grid place-content-center">
        <DotSpinner />
      </div>
    );
  }

  //If we don't have a prospectId, we just show that the company no longer exists. If we have one, we ask to link it to another one
  if (!selectedProspectId || !prospect?.data.data) {
    return (
      <FlexDiv direction="column" style={{ rowGap: 20 }}>
        <IllustrationAccountNotExisting />
        <Typography
          fontSize={16}
          lineHeight={'24px'}
          fontWeight={600}
          width={240}
          textAlign={'center'}
        >
          This company does not exist anymore
        </Typography>
        {!!taskId && (
          <TaskExecutionFooterActions taskId={taskId} className="mr-0" />
        )}
      </FlexDiv>
    );
  }

  return (
    <>
      {showCompanyAssign &&
        !showCompanyCreateAssign &&
        createPortal(
          <CompanyAssignModal
            prospectId={prospect.data.data.objectId}
            close={() => {
              setShowCompanyAssign(false);
            }}
            openCreateCompany={(search = '') => {
              setInitialNameCreateNewAccount(search);
              setShowCompanyCreateAssign(true);
            }}
          />,
          document.body,
        )}
      {showCompanyCreateAssign && (
        <AddCompanyDrawer
          onClose={() => setShowCompanyCreateAssign(false)}
          onAssigned={(newAccountId) => {
            afterContactReassigned(newAccountId);
            setShowCompanyAssign(false);
          }}
          prospectId={prospect.data.data.objectId}
          initialName={initialNameCreateNewAccount}
        />
      )}
      <FlexDiv
        direction="column"
        style={{
          rowGap: 20,
        }}
      >
        <IllustrationAccountNotExisting />
        <Typography
          fontSize={16}
          lineHeight={'24px'}
          fontWeight={600}
          maxWidth={510}
          textAlign={'center'}
        >
          The company {prospect.data.data.account?.name ?? ''} linked to this
          contact has been deleted. Please assign a different company or create
          a new one
        </Typography>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <CancelButton
            style={{
              margin: 0,
              width: 166,
            }}
            onClick={() => setShowCompanyAssign(true)}
          >
            Link to existing
          </CancelButton>
          <MainButton
            style={{
              margin: 0,
              width: 166,
            }}
            onClick={() => {
              setInitialNameCreateNewAccount('');
              setShowCompanyCreateAssign(true);
            }}
          >
            Create new
          </MainButton>
        </div>
        {!!taskId && (
          <TaskExecutionFooterActions taskId={taskId} className="mr-0" />
        )}
      </FlexDiv>
    </>
  );
};

export default CompanyDeletedDrawerView;
